import type { Auftrag, AuftragsStatus, AuftragsTyp } from '@awf/shared';
import { AUFTRAGSTYPEN, type Schritt } from '../auftragstypen.js';
import { normalisiere } from './status.js';

export type PhasenZustand = 'erledigt' | 'aktiv' | 'offen' | 'gescheitert';

export interface Phase {
  schritt: Schritt;
  zustand: PhasenZustand;
  /** Der aktive Entwurf ist eine Korrekturrunde, nicht der erste Wurf. */
  ueberarbeitung: boolean;
  /**
   * Das Kreuz steht fuer einen Abbruch, nicht fuer einen Fehler. Die Optik bleibt dieselbe
   * (`gescheitert`), nur der Vorlesetext unterscheidet (Codex-Gate PR #50).
   */
  abgebrochen: boolean;
}

function zustaende(
  schritte: readonly Schritt[],
  index: number,
  amIndex: PhasenZustand,
  { ueberarbeitung = false, abgebrochen = false } = {},
): Phase[] {
  return schritte.map((schritt, i) => ({
    schritt,
    zustand: i < index ? 'erledigt' : i === index ? amIndex : 'offen',
    ueberarbeitung: i === index && ueberarbeitung,
    abgebrochen: i === index && abgebrochen,
  }));
}

/**
 * Null heisst "keine Leiste": lieber keine Aussage als eine falsche. Bei einem Fehler steht
 * das Kreuz nur dort, wo der Verlauf es belegt -- der Status `fehlgeschlagen` selbst sagt
 * nicht, in welchem Schritt es geschah.
 */
export function phasenZustaende(
  typ: AuftragsTyp,
  status: AuftragsStatus,
  verlauf?: Auftrag['verlauf'],
): Phase[] | null {
  const schritte: readonly Schritt[] = AUFTRAGSTYPEN[typ].schritte;
  const indexVon = (s: AuftragsStatus) => schritte.findIndex((x) => x.status === normalisiere(s));

  if (status === 'fehlgeschlagen' || status === 'abgebrochen') {
    const letzter = [...(verlauf ?? [])].reverse().find((e) => indexVon(e.status) !== -1);
    if (!letzter) return null;
    return zustaende(schritte, indexVon(letzter.status), 'gescheitert', {
      abgebrochen: status === 'abgebrochen',
    });
  }

  const index = indexVon(status);
  if (index === -1) return null;
  if (status === 'fertig') return zustaende(schritte, schritte.length, 'erledigt');
  return zustaende(schritte, index, 'aktiv', { ueberarbeitung: status === 'in_ueberarbeitung' });
}
